import React from 'react';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import cx from 'classnames';
import { ReactComponent as SupportIcon } from 'assets/icons/support.svg';
import { ReactComponent as TreatmentIcon } from 'assets/icons/treatment_icon.svg';
import { ReactComponent as HouseIcon } from 'assets/icons/house.svg';
import { ReactComponent as TinaIcon } from 'assets/icons/tina.svg';
import { ROUTE_PATH, ROUTE_NAME } from 'utils/constants/router';
// eslint-disable-next-line import/no-cycle
import app from 'new-ui/app';
import { GA_HCP } from 'modules/ga/hcp';
import { useTranslation } from 'utils/modifiedTranslation';
import { REMATCH_URL_SEARCH_PARAM, USER_TYPE_URL_SEARCH_PARAM } from 'utils/constants/urlSearchParameters';
import { preserveQueryParam } from 'functions/preserveQueryParam';
import configs from 'configs';
import { createUserFinance } from 'pages/FinanceSupport/createUserFinance';
import { VERSIONS, TAB_IDS } from './constants';
import styles from './headerTabs.module.css';

const {
  DEFAULT: DEFAULT_VERSION,
  HCP: HCP_VERSION,
} = VERSIONS;

const SUPPORTED_VERSIONS = [DEFAULT_VERSION, HCP_VERSION];

const getIsHcp = () => (
  app.isCurrentPage(ROUTE_NAME.PUBLIC_FINANCE) || app.isCurrentPage(ROUTE_NAME.SEARCH) || app.isCurrentPage(ROUTE_NAME.PATIENTS)
);

const getIsSearchPage = () => (
  app.isCurrentPage(ROUTE_NAME.PUBLIC_FINANCE) || app.isCurrentPage(ROUTE_NAME.SEARCH)
);

const GA = {
  category: 'Finance',
  events: {
    clickFinancialTab: 'Clicking on the “Financial support” tab',
    clickTreatmentTab: 'Clicking on the “Treatment options” tab',
    clickSymptomCareTab: 'Clicking on the “Symptom Care” tab',
    clickArticlesTab: 'Clicking on the “Articles” tab',
  },
};

const GA_CHAT = {
  category: 'Chat',
  events: {
    clickSpeakWithTinaTab: 'Clicking on the “Speak with Tina” tab',
  },
};

const GET_IS_TAB_ACTIVE_BY_TAB_ID = {
  [TAB_IDS.FEED]: () => app.isCurrentPage(ROUTE_NAME.FEED),
  [TAB_IDS.TREATMENT_OPTIONS]: () => (
    ((getIsSearchPage() || getIsHcp()) && app.isCurrentPage(ROUTE_NAME.SEARCH)) || app.isCurrentPage(ROUTE_NAME.RESULTS)
  ),
  [TAB_IDS.SUPPORT_PROGRAMS]: () => (
    ((getIsSearchPage() || getIsHcp()) && app.isCurrentPage(ROUTE_NAME.PUBLIC_FINANCE)) || app.isCurrentPage(ROUTE_NAME.FINANCE)
  ),
  [TAB_IDS.SPEAK_WITH_TINA]: () => app.isCurrentPage(ROUTE_NAME.CHAT),
  [TAB_IDS.ARTICLES]: () => app.isCurrentPage(ROUTE_NAME.ARTICLES),
};

const CLASS_BY_VERSION = {
  [DEFAULT_VERSION]: styles.defaultTabs,
  [HCP_VERSION]: styles.hcpTabs,
};

const getIsTabActive = (tabId) => (GET_IS_TAB_ACTIVE_BY_TAB_ID[tabId] ?? (() => false))();

export const HeaderTabs = ({
  version, tabIds, isMobileMenuOpen, onMenuItemClick, isAuthenticated,
}) => {
  const isHcp = getIsHcp();
  const isSearchPage = getIsSearchPage();

  const { t } = useTranslation();

  if (!SUPPORTED_VERSIONS.includes(version)) {
    return null;
  }

  return (
    <div className={cx(styles.root, CLASS_BY_VERSION[version], { [styles.mobileMenuOpen]: isMobileMenuOpen })}>
      {tabIds.includes(TAB_IDS.FEED) && (
        <div
          className={cx(styles.tab, { [styles.activeTab]: getIsTabActive(TAB_IDS.FEED) })}
          onClick={() => onMenuItemClick((dispatch) => {
            dispatch(push(ROUTE_PATH[ROUTE_NAME.FEED]));
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickFinancialTab, { name: 'Feed tab clicked' });
          })}
        >
          <div className={styles.tabIcon}>
            <HouseIcon />
          </div>
          <div className={styles.tabTitle}>
            {t('general.feed')}
          </div>
        </div>
      )}
      {tabIds.includes(TAB_IDS.TREATMENT_OPTIONS) && (
        <div
          className={cx(styles.tab, { [styles.activeTab]: getIsTabActive(TAB_IDS.TREATMENT_OPTIONS) })}
          onClick={() => onMenuItemClick((dispatch) => {
            if (app.isCurrentPage(ROUTE_NAME.FINANCE) && !isAuthenticated) {
              createUserFinance({
                redirectTo: `${ROUTE_PATH[ROUTE_NAME.RESULTS]}?${REMATCH_URL_SEARCH_PARAM}=1`,
                subtitle: t('support_programs.create_user_subtitle_header'),
              });
              return;
            }
            if (isSearchPage || isHcp) {
              dispatch(push(preserveQueryParam(ROUTE_PATH[ROUTE_NAME.SEARCH], {
                pick: [USER_TYPE_URL_SEARCH_PARAM],
              })));
              app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.TREATMENTS_TAB_CLICKED);
            } else {
              dispatch(push(ROUTE_PATH[ROUTE_NAME.RESULTS]));
            }
          })}
        >
          <div className={styles.tabIcon}>
            <TreatmentIcon />
          </div>
          <div
            className={styles.tabTitle}
            onClick={() => {
              app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickTreatmentTab, { name: 'Treatment options tab' });
            }}
          >
            {t('general.treatment_options')}
          </div>
        </div>
      )}
      {tabIds.includes(TAB_IDS.SUPPORT_PROGRAMS) && (
        <div
          className={cx(styles.tab, { [styles.activeTab]: getIsTabActive(TAB_IDS.SUPPORT_PROGRAMS) })}
          onClick={() => onMenuItemClick((dispatch) => {
            if (isSearchPage || isHcp) {
              dispatch(push(preserveQueryParam(ROUTE_PATH[ROUTE_NAME.PUBLIC_FINANCE], {
                pick: [USER_TYPE_URL_SEARCH_PARAM],
              })));
              app.sendGoogleAnalyticsEvent(GA_HCP.category, GA_HCP.events.FINANCE_TAB_CLICKED);
            } else {
              dispatch(push(ROUTE_PATH[ROUTE_NAME.FINANCE]));
            }
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickFinancialTab, { name: 'Financial support tab clicked' });
          })}
        >
          <div className={styles.tabIcon}>
            <SupportIcon />
          </div>
          <div className={styles.tabTitle}>
            {t('general.support_programs')}
          </div>
        </div>
      )}

      {tabIds.includes(TAB_IDS.SYMPTOM_CARE) && (
        <div
          className={cx(styles.tab)}
          onClick={() => onMenuItemClick(() => {
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickSymptomCareTab, { name: 'Symptom Care tab clicked' });
            const condition = app.user?.personal?.condition;
            const url = new URL(`${configs.sideEffectsUrl}/search`);
            if (condition) {
              url.searchParams.append('cancer_type', condition);
            }
            url.searchParams.append('user', 'registered');
            window.open(url.toString(), '_blank');
          })}
        >
          <div className={styles.tabIcon}>
            <TreatmentIcon />
          </div>
          <div className={styles.tabTitle}>{t('general.symptom_care')}</div>
        </div>
      )}

      {tabIds.includes(TAB_IDS.ARTICLES) && (
        <div
          className={cx(styles.tab, { [styles.activeTab]: getIsTabActive(TAB_IDS.ARTICLES) })}
          onClick={() => onMenuItemClick((dispatch) => {
            app.sendGoogleAnalyticsEvent(GA.category, GA.events.clickArticlesTab, { name: 'Articles tab clicked' });
            dispatch(push(ROUTE_PATH[ROUTE_NAME.ARTICLES]));
          })}
        >
          <div className={styles.tabTitle}>{t('general.articles')}</div>
        </div>
      )}

      {tabIds.includes(TAB_IDS.SPEAK_WITH_TINA) && (
        <div
          className={cx(styles.tab, styles.tinaTab, { [styles.activeTab]: getIsTabActive(TAB_IDS.SPEAK_WITH_TINA) })}
          onClick={() => onMenuItemClick((dispatch) => {
            app.sendGoogleAnalyticsEvent(GA_CHAT.category, GA_CHAT.events.clickSpeakWithTinaTab, { name: 'speack with tina tab clicked' });
            dispatch(push(ROUTE_PATH[ROUTE_NAME.CHAT]));
          })}
        >
          <div className={styles.tabIcon}>
            <TinaIcon />
          </div>
          <div className={styles.tabTitle}>
            {t('general.speak_with_tina')}
            <span className={styles.tinaEmoji}>😊</span>
          </div>
        </div>
      )}

    </div>
  );
};

HeaderTabs.propTypes = {
  version: PropTypes.oneOf(SUPPORTED_VERSIONS).isRequired,
  tabIds: PropTypes.arrayOf(PropTypes.oneOf(Object.values(TAB_IDS))).isRequired,
  isMobileMenuOpen: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  onMenuItemClick: PropTypes.func.isRequired,
};
